import { courseLabel } from "utils/courses";

// Pure helpers for the Bulk Message page (kept out of index.js so they can be unit-tested).

// The Region filter works from each contact's stored region, country or phone code. Contacts
// none of those can place are left out of a region-filtered send; this says how many, and from
// which sources, so the count on screen is explained. "" when nobody was left out.
export function regionUnknownNote(regionUnknown, sourceLabels) {
  const entries = Object.entries(regionUnknown || {}).filter(([, n]) => n > 0);
  if (!entries.length) return "";
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const list = entries.map(([key, n]) => `${sourceLabels[key] || key} (${n})`).join(", ");
  return `${total} contact${
    total === 1 ? " was" : "s were"
  } left out because their country isn't known: ${list}.`;
}

// The audience reply carries, per channel, how many messages a send would make (`send`) and why
// the rest won't. Each channel is de-duplicated by its own address on the server, so these are
// exactly what the send does — the numbers shown on the button and in the confirm dialog.
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const CHANNEL_NOUN = { email: ["email", "emails"], sms: ["text message", "text messages"] };

export function plannedTotal(channels, activeChannels) {
  return activeChannels.reduce((sum, ch) => sum + (channels?.[ch]?.send || 0), 0);
}

export function plannedSummary(channels, activeChannels) {
  return activeChannels
    .filter((ch) => channels?.[ch])
    .map((ch) => plural(channels[ch].send, ...CHANNEL_NOUN[ch]))
    .join(" · ");
}

export function skippedSummary(channels, activeChannels) {
  const parts = activeChannels
    .filter((ch) => channels?.[ch])
    .map((ch) => {
      const c = channels[ch];
      const address = ch === "email" ? ["address", "addresses"] : ["number", "numbers"];
      const reasons = [
        c.noAddress && `${c.noAddress} with no usable ${ch === "email" ? "email" : "number"}`,
        c.noConsent && `${c.noConsent} without consent`,
        c.excluded && `${c.excluded} excluded`,
        c.duplicates && plural(c.duplicates, `duplicate ${address[0]}`, `duplicate ${address[1]}`),
      ].filter(Boolean);
      return reasons.length ? `${ch === "email" ? "email" : "SMS"}: ${reasons.join(", ")}` : "";
    })
    .filter(Boolean);
  return parts.length ? `Not sent — ${parts.join("; ")}.` : "";
}

// What the "Send Test" button reports. Tests go by email only; when SMS is also on, say that
// the SMS was not sent rather than implying it was.
export function testSentMessage(data, testEmail) {
  const sample = `${data?.sampleContact?.name || "N/A"} (${data?.sampleContact?.source || ""})`;
  const base = `Test email sent to ${testEmail} using sample contact: ${sample}`;
  return data?.smsNotSent ? `${base}. The SMS is not sent as a test — check it in Preview.` : base;
}

export function makeSendId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// The server refuses a sendId it has already seen, so the id must stay the same for as long as
// the message (audience, channels, content) is unchanged and change as soon as anything does.
// `prev` is the last { fingerprint, id } (or null); returns prev itself when nothing changed.
export function sendIdFor(prev, payload, makeId = makeSendId) {
  const fingerprint = JSON.stringify(payload);
  if (prev && prev.fingerprint === fingerprint) return prev;
  return { fingerprint, id: makeId() };
}

// What to tell the user when the send request fails. With no reply at all (browser or proxy
// timeout, dropped connection, gateway error) the server may well still be sending, so this
// must not read as "failed" — that is what invited a second send.
export function sendFailureNotice(err) {
  const status = err?.response?.status;
  if (!err?.response || [502, 503, 504].includes(status)) {
    return {
      severity: "warning",
      title: "The send may still be running",
      text:
        "No reply came back from the server, but the messages may still be going out. Do not send it again: " +
        "wait a few minutes, then check with a recipient. Sending this same message again is blocked so nobody gets it twice.",
    };
  }
  if (status === 409) {
    return {
      severity: "info",
      title: "Not sent again",
      text: err.response.data?.error || "This message was not sent again.",
    };
  }
  return { severity: "error", title: "Send failed", text: err.response.data?.error || err.message };
}

// The placeholder chips. Bulk Message fills exactly the Autoresponder's placeholders
// (server/services/autoresponderProcessor.js interpolate / KNOWN_TAGS).
export const PLACEHOLDERS = [
  { tag: "{{name}}", desc: "Full name" },
  { tag: "{{first_name}}", desc: "First name" },
  { tag: "{{email}}", desc: "Email address" },
  { tag: "{{phone}}", desc: "Phone number" },
  { tag: "{{region}}", desc: "Country code, e.g. uk or ng" },
  { tag: "{{city}}", desc: "City (landing applications)" },
  { tag: "{{ecosystem}}", desc: "Tech ecosystem (landing applications)" },
  { tag: "{{paymentPreference}}", desc: "Payment preference (landing applications)" },
  { tag: "{{service}}", desc: "Service (consultations)" },
  { tag: "{{company}}", desc: "Company (consultations)" },
  { tag: "{{preferredDate}}", desc: "Preferred date (consultations)" },
  { tag: "{{preferredTime}}", desc: "Preferred time (consultations)" },
  { tag: "{{subject}}", desc: "Subject (contact form)" },
  { tag: "{{course}}", desc: "Course name" },
  { tag: "{{bookingLink}}", desc: "Booking URL (the course's own, if set)" },
  { tag: "{{whatsappLink}}", desc: "WhatsApp link" },
  { tag: "{{cohortDate}}", desc: "Next cohort date" },
  { tag: "{{spotsRemaining}}", desc: "Spots left" },
  { tag: "{{repName}}", desc: "Sales rep name" },
  { tag: "{{currency}}", desc: "UK price for UK contacts, Nigerian price for everyone else" },
  { tag: "{{zoomUrl}}", desc: "Webinar join link" },
  { tag: "{{zoomId}}", desc: "Webinar meeting ID" },
  { tag: "{{zoomPasscode}}", desc: "Webinar passcode" },
  { tag: "{{webinarDate}}", desc: "Webinar date" },
  { tag: "{{webinarTime}}", desc: "Webinar time" },
  { tag: "{{webinarDay}}", desc: "Webinar weekday, e.g. Sunday" },
  { tag: "{{webinarWhatsapp}}", desc: "Webinar WhatsApp group link" },
  { tag: "{{callDate}}", desc: "Clarity call date (visitor's zone)" },
  { tag: "{{callTime}}", desc: "Clarity call time (visitor's zone)" },
  { tag: "{{manageLink}}", desc: "Clarity call reschedule/cancel link" },
  { tag: "{{file:slug}}", desc: "File download URL (use the file's slug)" },
];

// Placeholders the server would not fill (from Preview). "" when there are none.
export function unknownPlaceholdersNote(list) {
  if (!list || !list.length) return "";
  return `Not recognised, so they would go out as typed: ${list.join(
    ", "
  )}. Fix or remove them — the send is refused until you do.`;
}

const statusLabel = (s) => s.toLowerCase().replace("_", " ");

// The confirm dialog's "Filters:" line, in words: course title (not slug), the booking status
// whenever clarity calls are in the audience, region and dates. "" when nothing narrows it.
export function filterSummary(filter, sources) {
  const parts = [];
  if (filter.region && filter.region !== "all")
    parts.push(`Region: ${filter.region.toUpperCase()}`);
  if (filter.course && filter.course !== "all") parts.push(`Course: ${courseLabel(filter.course)}`);
  if (sources.includes("clarity_call_booking")) {
    parts.push(
      `Booking status: ${
        filter.bookingStatuses?.length
          ? filter.bookingStatuses.map(statusLabel).join(", ")
          : "all except cancelled"
      }`
    );
  }
  if (filter.dateFrom) parts.push(`From ${filter.dateFrom}`);
  if (filter.dateTo) parts.push(`To ${filter.dateTo}`);
  return parts.join(" · ");
}
